import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import styled from "styled-components"
import { useScrollLock } from "../../../hooks/useScrollLock"
import { showModal } from "../../../features/modal/modalSlice"
import PageTopTitle from "../../shared/PageTopTitle"
import { HiMiniArrowLongRight } from "react-icons/hi2";
import RouteLine from "../../shipmentOperation/RouteLine"
import Btn from "../../shared/button/Btn"
import SmallSpinner from "../../shared/spinners/SmallSpinner"
import {
    forceReleaseVehicleClaim,
    resetRedirectPath,
    resetShipmentDetailsForManage,
} from "../../../features/shipmentOperation/shipmentOperationSlice"

const unwrapCollection = (value) => {
    if (Array.isArray(value)) return value
    if (Array.isArray(value?.$values)) return value.$values
    return []
}

const isVehicleClaimMode = (value) =>
    Number(value) === 1 || String(value ?? "").replace(/[\s_-]/g, "").toLowerCase() === "vehicleclaim"

const formatUtcDate = (value) => value
    ? new Date(value).toLocaleString("tr-TR")
    : "-"

const ModalContainer = styled.div`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: rgba(0,0,0,0.6);
    display: flex;
    justify-content: center;
    z-index: 26;
    padding-top: 80px;

    .modal{
        width: 900px;
        max-width: calc(100vw - 40px);
        height: min(800px, calc(100vh - 100px));
        max-height: 800px;
        color: ${({ theme }) => theme.mainTextColor};
        background-color: ${({ theme }) => theme.mainBgColor};
        border: 1px solid;
        border-color: ${({ theme }) => theme.mainBorderColor};
        padding-top: 20px;
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;

        .content{
            padding: 40px;
            width: calc(100% - 80px);
            box-sizing: content-box;
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;

            .top-container{
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid var(--color-main);
                width: 100%;
                padding-bottom: 5px;

                .driver{
                    color: ${({theme}) => theme.mainTextColor};
                    font-size: var(--font-size-md);
                    font-weight: var(--font-weight-lg);
                }

                .route-short{
                    display: flex;
                    align-items: center;
                    gap: 10px;

                    .route-short-item{
                        color: ${({theme}) => theme.mainTextColor};
                        font-size: var(--font-size-md);
                        font-weight: var(--font-weight-lg);
                    }
                }
            }

            .route-container{
                display: flex;
                flex-direction: column;
                gap: 20px;
                width: 100%;
                margin-top: 10px;
                height: auto;

                .route-item{
                    width: 100px;
                    height: 100px;
                    border: 1px solid ${({theme}) => theme.mainTextColor};
                    border-radius: 5px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 20px;
                }
            }

            .assignment-panel, .vehicle-history{
                width: 100%;
                box-sizing: border-box;
                border: 1px solid ${({theme}) => theme.mainBorderColor};
                border-radius: 8px;
                padding: 14px;
            }

            .assignment-panel{
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 10px 18px;
                font-size: var(--font-size-sm);

                .full-row{
                    grid-column: 1 / -1;
                }

                strong{
                    margin-right: 5px;
                }
            }

            .vehicle-history{
                h4{
                    margin: 0 0 10px;
                }

                .history-row{
                    display: grid;
                    grid-template-columns: 1.3fr 1fr 1.2fr;
                    gap: 12px;
                    padding: 9px 0;
                    border-top: 1px solid ${({theme}) => theme.mainBorderColor};
                    font-size: var(--font-size-sm);
                }

                .active-session{
                    color: #E85446;
                    font-weight: var(--font-weight-lg);
                }

                .history-driver, .history-release{
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }

                .history-secondary{
                    opacity: 0.75;
                    font-size: 11px;
                }
            }

            .force-release{
                width: 100%;
                box-sizing: border-box;
                padding: 14px;
                border: 1px solid #E85446;
                border-radius: 8px;

                textarea{
                    width: 100%;
                    box-sizing: border-box;
                    min-height: 72px;
                    margin: 10px 0 4px;
                    padding: 9px;
                    resize: vertical;
                    color: ${({theme}) => theme.mainTextColor};
                    background: ${({theme}) => theme.mainBgColor};
                    border: 1px solid ${({theme}) => theme.mainBorderColor};
                    border-radius: 6px;
                }

                .reason-count{
                    display: block;
                    margin-bottom: 10px;
                    text-align: right;
                    font-size: 12px;
                }

                .force-actions{
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }
            }

            .detail-status{
                padding: 30px;
                text-align: center;
            }
        }
    }
`

const ShipmentOperationModal = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const {modalLocation } = useSelector(state => state.modal)

    const {
        shipmentDetailsForManage,
        redirectPath,
        isOperationResultsSuccess,
        isLoadingShipmentDetailsForManage,
        shipmentDetailsForManageError,
        isLoadingForceReleaseVehicleClaim,
    } = useSelector(state => state.shipmentOperation)
    const [forceReleaseConfirmationVisible, setForceReleaseConfirmationVisible] = useState(false)
    const [forceReleaseReason, setForceReleaseReason] = useState("")

    const routePoints = unwrapCollection(shipmentDetailsForManage?.routePoints)
    const vehicleDriverHistory = unwrapCollection(shipmentDetailsForManage?.vehicleDriverHistory)
    const vehicleClaimMode = isVehicleClaimMode(shipmentDetailsForManage?.driverAssignmentMode)
    const activeSession = vehicleDriverHistory.find((session) =>
        (session?.releasedAtUtc ?? session?.ReleasedAtUtc) == null
    ) ?? null
    const truckPlate = shipmentDetailsForManage?.truckPlateNumber ?? "-"
    const trailerPlate = shipmentDetailsForManage?.trailerPlateNumber ?? null

    const assignmentStatus = !vehicleClaimMode
        ? "Doğrudan sürücüye atandı"
        : activeSession
            ? `${activeSession?.driverName || activeSession?.driverPhoneNumber || "Mobil sürücü"} taşıma setini kullanıyor`
            : vehicleDriverHistory.length > 0
                ? "Taşıma seti yeniden teslim alınmayı bekliyor"
                : "Mobil sürücünün teslim alması bekleniyor"

    const { lockScroll, unlockScroll } = useScrollLock();

    useEffect(() => {
        lockScroll()

        return () => {
            unlockScroll()
        }
    }, [lockScroll, unlockScroll])

    useEffect(() => {
        if (location.pathname !== modalLocation) {
            dispatch(showModal(null))
        }
    }, [dispatch, location.pathname, modalLocation])

    useEffect(() => {
        dispatch(resetRedirectPath())
        return () => dispatch(resetShipmentDetailsForManage())
    },[dispatch])

    const handleClose = () => {
        if(isOperationResultsSuccess && redirectPath && location.pathname !== redirectPath){
            navigate(redirectPath)
        }
        else{
            dispatch(showModal(null))
        }
    }

    const handleForceRelease = async () => {
        if (!shipmentDetailsForManage?.id || forceReleaseReason.length > 80) return

        try {
            await dispatch(forceReleaseVehicleClaim({
                shipmentId: shipmentDetailsForManage.id,
                reason: forceReleaseReason,
            })).unwrap()
            setForceReleaseConfirmationVisible(false)
            setForceReleaseReason("")
        } catch {
            // API hata mesajı thunk/interceptor tarafından gösterilir.
        }
    }

    return (
        <ModalContainer>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <PageTopTitle title={"Sevkiyatı Yönet"}/>
                <div className="content">
                    {isLoadingShipmentDetailsForManage && !shipmentDetailsForManage ? (
                        <div className="detail-status"><SmallSpinner size="38px" color="#fff" bgColor="#E85446"/></div>
                    ) : shipmentDetailsForManageError && !shipmentDetailsForManage ? (
                        <div className="detail-status">{shipmentDetailsForManageError}</div>
                    ) : !shipmentDetailsForManage ? (
                        <div className="detail-status">Sevkiyat detayı bulunamadı.</div>
                    ) : (
                        <>
                            <div className="top-container">
                                <div className="driver">
                                    Sürücü: {shipmentDetailsForManage.driverName || (vehicleClaimMode ? "Teslim alınması bekleniyor" : "-")}
                                </div>
                                <div className="route-short">
                                    <div className="route-short-item">{routePoints[0]?.location ?? "-"}</div>
                                    <HiMiniArrowLongRight className="arr-icon"/>
                                    <div className="route-short-item">{routePoints[routePoints.length - 1]?.location ?? "-"}</div>
                                </div>
                            </div>

                            <div className="assignment-panel">
                                <div><strong>Atama şekli:</strong>{vehicleClaimMode ? "Taşıma seti teslim alma" : "Doğrudan sürücü"}</div>
                                <div><strong>Durum:</strong>{assignmentStatus}</div>
                                <div><strong>Çekici/Araç:</strong>{truckPlate}</div>
                                <div><strong>Dorse:</strong>{trailerPlate || "Yok"}</div>
                                <div><strong>Konteyner:</strong>{shipmentDetailsForManage.containerNumber || "-"}</div>
                                <div><strong>Mühür:</strong>{shipmentDetailsForManage.sealNumber || "-"}</div>
                                {shipmentDetailsForManage.assignedSubDriverUserId && (
                                    <div className="full-row"><strong>Teslim alma kısıtı:</strong>Yalnızca seçilen alt sürücü teslim alabilir</div>
                                )}
                            </div>

                            {(vehicleClaimMode || vehicleDriverHistory.length > 0) && (
                                <div className="vehicle-history">
                                    <h4>Taşıma Seti Kullanım Geçmişi</h4>
                                    {vehicleDriverHistory.length === 0
                                        ? <div>Henüz teslim alma kaydı bulunmuyor.</div>
                                        : vehicleDriverHistory.map((session) => {
                                            const releasedAtUtc = session?.releasedAtUtc ?? session?.ReleasedAtUtc
                                            const releaseReason = session?.releaseReason ?? session?.ReleaseReason
                                            return (
                                                <div className="history-row" key={session?.id ?? session?.Id}>
                                                    <span className="history-driver">
                                                        <span>{session?.driverName || session?.driverPhoneNumber || session?.driverUserId}</span>
                                                        {session?.driverName && session?.driverPhoneNumber && (
                                                            <span className="history-secondary">{session.driverPhoneNumber}</span>
                                                        )}
                                                    </span>
                                                    <span>Alış: {formatUtcDate(session?.claimedAtUtc ?? session?.ClaimedAtUtc)}</span>
                                                    <span className={`history-release ${!releasedAtUtc ? "active-session" : ""}`}>
                                                        <span>{releasedAtUtc ? `Bırakış: ${formatUtcDate(releasedAtUtc)}` : "Set halen sürücüde"}</span>
                                                        {releaseReason && (
                                                            <span className="history-secondary">Neden: {releaseReason}</span>
                                                        )}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                </div>
                            )}

                            {vehicleClaimMode && activeSession && !forceReleaseConfirmationVisible && (
                                <Btn
                                    handleClick={() => setForceReleaseConfirmationVisible(true)}
                                    width="260px"
                                    bgColor="#E85446"
                                    borderColor="transparent"
                                    color="#fff"
                                    padding="10px"
                                >Sürücüyü Setten Çıkar</Btn>
                            )}

                            {vehicleClaimMode && activeSession && forceReleaseConfirmationVisible && (
                                <div className="force-release">
                                    <strong>Sürücü taşıma setinden çıkarılsın mı?</strong>
                                    <div>Set ve sevkiyatlar kapanmaz; başka uygun bir sürücü seti yeniden teslim alabilir.</div>
                                    <textarea
                                        maxLength={80}
                                        value={forceReleaseReason}
                                        placeholder="Sebep (opsiyonel, en fazla 80 karakter)"
                                        onChange={(event) => setForceReleaseReason(event.target.value)}
                                    />
                                    <span className="reason-count">{forceReleaseReason.length}/80</span>
                                    <div className="force-actions">
                                        <Btn
                                            handleClick={() => setForceReleaseConfirmationVisible(false)}
                                            width="120px"
                                            bgColor="#333"
                                            color="#fff"
                                            padding="8px"
                                            isDisabled={isLoadingForceReleaseVehicleClaim}
                                        >Vazgeç</Btn>
                                        <Btn
                                            handleClick={handleForceRelease}
                                            width="190px"
                                            bgColor="#E85446"
                                            color="#fff"
                                            padding="8px"
                                            isDisabled={isLoadingForceReleaseVehicleClaim || forceReleaseReason.length > 80}
                                        >{isLoadingForceReleaseVehicleClaim ? "İşleniyor..." : "Sürücüyü Çıkar"}</Btn>
                                    </div>
                                </div>
                            )}

                            <div className="route-container">
                                <RouteLine route={routePoints}/>
                            </div>
                        </>
                    )}
                </div>

                <div className="btn-container">
                    <Btn handleClick={() => handleClose()} width={"180px"} bgColor="#333" borderColor="transparent" color="#fff" padding="10px">Kapat</Btn>
                </div>
            </div>
        </ModalContainer>
    )
}

export default ShipmentOperationModal
